import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { guestAPI, fmtINR } from "./guestApi";
import CountUp from "@/pages/operations/CountUp";
import {
  UserRound, Star, Repeat, UserPlus, PlaneTakeoff, PlaneLanding,
  Cake, Heart, Building2, Ban, Smile, Wallet,
} from "lucide-react";

const Tile = ({ label, value, icon: Icon, accent = "text-hocp-gold", testid, to }) => {
  const body = (
    <div data-testid={testid}
      className="on-dark relative overflow-hidden bg-gradient-to-br from-[#0F1530] to-[#070B1F] rounded-2xl p-5 border border-white/10">
      <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-hocp-gold/5 blur-2xl pointer-events-none"/>
      <div className={`w-10 h-10 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center ${accent} relative`}>
        <Icon className="w-5 h-5" strokeWidth={1.75}/>
      </div>
      <div className="mt-4 relative">
        <div className="font-display text-[26px] font-semibold text-white tabular-nums leading-none">{value}</div>
        <div className="text-[11px] text-slate-200 font-medium mt-2 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
};

export default function GuestDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => guestAPI.dashboard().then(setD).catch(()=>{});
    load(); const iv = setInterval(load, 30000); return () => clearInterval(iv);
  }, []);
  const k = d?.kpis || {};

  return (
    <div className="space-y-5" data-testid="guest-dashboard">
      <div className="on-dark relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1020] via-[#070B1F] to-[#0F1530] p-6 border border-white/10">
        <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-hocp-gold/10 blur-3xl pointer-events-none"/>
        <div className="text-[11px] uppercase tracking-widest text-slate-300 font-medium flex items-center gap-2">
          <UserRound className="w-3.5 h-3.5"/> Guest Intelligence
          <span className="text-hocp-gold font-semibold tabular-nums">{d?.business_date || "—"}</span>
        </div>
        <div className="mt-3 font-display text-[42px] font-bold text-white tabular-nums leading-none">
          <CountUp value={Number(k.staying)||0}/>
          <span className="text-slate-400 text-[22px]"> guests in-house</span>
        </div>
        <div className="text-[13px] text-slate-300 mt-2">
          {k.arrivals || 0} arrivals · {k.departures || 0} departures · {k.vip || 0} VIP · Lifetime {fmtINR(k.lifetime_revenue || 0)}
        </div>
        <div className="mt-5 flex gap-2">
          <Link to="/guests/directory" data-testid="guest-qa-directory"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[12.5px] font-semibold hover:brightness-95 transition">
            <UserRound className="w-4 h-4"/> Open Directory
          </Link>
          <Link to="/guests/directory?new=1" data-testid="guest-qa-new"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-white/10 text-white text-[12.5px] font-medium hover:bg-white/15 border border-white/10">
            <UserPlus className="w-4 h-4"/> New Guest Profile
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Tile label="Guests Staying"    value={<CountUp value={Number(k.staying)||0}/>}    icon={UserRound}     testid="guest-kpi-staying"  accent="text-emerald-300"/>
        <Tile label="VIP Guests"        value={<CountUp value={Number(k.vip)||0}/>}        icon={Star}          testid="guest-kpi-vip"      accent="text-hocp-gold" to="/guests/directory?vip=1"/>
        <Tile label="Repeat Guests"     value={<CountUp value={Number(k.repeat)||0}/>}     icon={Repeat}        testid="guest-kpi-repeat"   accent="text-sky-300" to="/guests/directory?repeat=1"/>
        <Tile label="New (today)"       value={<CountUp value={Number(k.new_today)||0}/>}  icon={UserPlus}      testid="guest-kpi-new"      accent="text-cyan-300"/>
        <Tile label="Arrivals Today"    value={<CountUp value={Number(k.arrivals)||0}/>}   icon={PlaneLanding}  testid="guest-kpi-arrivals" accent="text-emerald-300"/>
        <Tile label="Departures Today"  value={<CountUp value={Number(k.departures)||0}/>} icon={PlaneTakeoff}  testid="guest-kpi-departures" accent="text-amber-300"/>
        <Tile label="Birthdays"         value={<CountUp value={Number(k.birthdays)||0}/>}  icon={Cake}          testid="guest-kpi-birthdays" accent="text-pink-300"/>
        <Tile label="Anniversaries"     value={<CountUp value={Number(k.anniversaries)||0}/>} icon={Heart}      testid="guest-kpi-anniversaries" accent="text-rose-300"/>
        <Tile label="Corporate"         value={<CountUp value={Number(k.corporate)||0}/>}  icon={Building2}     testid="guest-kpi-corporate" accent="text-violet-300" to="/guests/directory?corporate=1"/>
        <Tile label="Blacklist"         value={<CountUp value={Number(k.blacklist)||0}/>}  icon={Ban}           testid="guest-kpi-blacklist" accent={Number(k.blacklist) > 0 ? "text-rose-300" : "text-slate-400"} to="/guests/directory?blacklist=1"/>
        <Tile label="Satisfaction"      value={`${k.satisfaction || 0} / 5`}                   icon={Smile}         testid="guest-kpi-satisfaction" accent="text-emerald-300"/>
        <Tile label="Lifetime Revenue"  value={fmtINR(k.lifetime_revenue || 0)}             icon={Wallet}        testid="guest-kpi-revenue" accent="text-hocp-gold"/>
      </div>
    </div>
  );
}
